import { cn } from '@qr-front/ui/utils'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtomValue } from 'jotai'
import NextImage from 'next/image'

import { TUploadedMedia } from '@/services'

import {
	imageListAtom,
	imageLocalPreviewUrlsAtom,
} from '../../../contentFormSections'
import { socialMediaPrimaryColorAtom } from '../../SocialMedia/SocialMediaScreen/SocialMediaScreenState'
import { imagesPrimaryColorAtom } from './imagesScreenState'

const IMAGE_ORDER_TO_DEMO_IMAGE = {
	0: '/web-app/qr-preview-demos/images/image-1.png',
	1: '/web-app/qr-preview-demos/images/image-2.png',
	2: '/web-app/qr-preview-demos/images/image-3.png',
}

const IMAGE_ORDER_TO_SIZE = {
	0: { width: 170, height: 240 },
	1: { width: 204, height: 134 },
	2: { width: 204, height: 134 },
}

const SOCIAL_MEDIA_IMAGE_ORDER_TO_DEMO_IMAGE = {
	0: '/web-app/qr-preview-demos/social-media/image-1.png',
	1: '/web-app/qr-preview-demos/social-media/image-2.png',
	2: '/web-app/qr-preview-demos/social-media/image-3.png',
}

const SOCIAL_MEDIA_IMAGE_ORDER_TO_SIZE = {
	0: { width: 130, height: 130 },
	1: { width: 100, height: 100 },
	2: { width: 100, height: 100 },
}

const Image = ({
	image,
	localSrc,
	showOverlay,
	order,
	demoMode,
	qrType,
	rendersOnMobileShowcasePreview,
}: {
	image?: TUploadedMedia
	localSrc?: string | null
	showOverlay: () => void
	order: 0 | 1 | 2
	demoMode: boolean
	qrType?: QR_TYPE
	rendersOnMobileShowcasePreview?: boolean
}) => {
	const isSocialMediaQrType = qrType === QR_TYPE.SocialMedia
	const isImagesQrType = qrType === QR_TYPE.Images

	const imageOrderToDemoImage = isSocialMediaQrType
		? SOCIAL_MEDIA_IMAGE_ORDER_TO_DEMO_IMAGE
		: IMAGE_ORDER_TO_DEMO_IMAGE

	const imageOrderToSize = isSocialMediaQrType
		? SOCIAL_MEDIA_IMAGE_ORDER_TO_SIZE
		: IMAGE_ORDER_TO_SIZE

	const demoImage = (
		// @ts-expect-error no alt attribute
		<NextImage
			src={imageOrderToDemoImage[order]}
			{...imageOrderToSize[order]}
			loading="lazy"
			className="h-full w-full rounded-4 object-cover"
			decoding="async"
			fetchPriority="low"
		/>
	)

	const imageSrc = image?.publicId
		? `${image.bucketRootUrl}/${image.publicId}`
		: localSrc

	const actualImage = imageSrc ? (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			alt={image?.publicId ?? 'uploading'}
			src={imageSrc}
			onClick={showOverlay}
			className="h-full w-full rounded-4 object-cover"
		/>
	) : null

	const displayedImage = demoMode ? demoImage : actualImage

	const generatorImageClsList = [
		'z-[1] h-60 w-[170px]',
		'h-[204px] w-[134px] translate-x-[36px]',
		'h-[204px] w-[134px] translate-x-[-36px]',
	]

	const landingMobilePreviewImageQrTypeClsList = [
		'z-[1] h-60 w-[170px]',
		'h-[204px] w-[134px] translate-x-[76px]',
		'h-[204px] w-[134px] translate-x-[-76px]',
	]

	const socialMediaImageClsList = [
		'z-[1] size-[134px]',
		'size-26 translate-x-[54px]',
		'size-26 -translate-x-[54px]',
	]

	const defaultImageClsList =
		rendersOnMobileShowcasePreview && isImagesQrType
			? landingMobilePreviewImageQrTypeClsList
			: generatorImageClsList

	const imageClsList = isSocialMediaQrType
		? socialMediaImageClsList
		: defaultImageClsList

	const imageCls = imageClsList[order]

	const baseCls =
		'rounded-4 bg-white p-[2px] shadow-generator [grid-column:1] [grid-row:1]'

	const pointerCls = imageSrc ? undefined : 'pointer-events-none'

	return (
		<button
			onClick={() => {
				if (actualImage) showOverlay()
			}}
			className={cn(baseCls, pointerCls, imageCls)}
		>
			{displayedImage}
		</button>
	)
}

export const ImagesPreview = ({
	demoMode,
	qrType,
	showOverlay,
	rendersOnMobileShowcasePreview = false,
	removeTopSpace = false,
}: {
	demoMode: boolean
	qrType?: QR_TYPE
	showOverlay: () => void
	rendersOnMobileShowcasePreview?: boolean
	removeTopSpace?: boolean
}) => {
	const imageList = useAtomValue(imageListAtom)
	const localPreviewUrls = useAtomValue(imageLocalPreviewUrlsAtom)
	const isSocialMediaQrType = qrType === QR_TYPE.SocialMedia

	const firstImage = imageList[0]
	const secondImage = imageList[1]
	const thirdImage = imageList[2]

	const primaryColorAtom = isSocialMediaQrType
		? socialMediaPrimaryColorAtom
		: imagesPrimaryColorAtom

	const primaryColor = useAtomValue(primaryColorAtom)

	const baseCls = 'absolute w-full transition-colors'
	const imageCls = isSocialMediaQrType ? 'h-[71px]' : 'h-[109px]'

	return (
		<div className="relative grid place-items-center">
			{!removeTopSpace ? (
				<div
					className={cn(baseCls, imageCls)}
					style={{
						backgroundColor: primaryColor,
						top: -1,
					}}
				/>
			) : null}
			<Image
				image={firstImage}
				localSrc={localPreviewUrls[0]}
				showOverlay={showOverlay}
				order={0}
				demoMode={demoMode}
				qrType={qrType}
				rendersOnMobileShowcasePreview={rendersOnMobileShowcasePreview}
			/>
			{demoMode || secondImage || localPreviewUrls[1] ? (
				<Image
					image={secondImage}
					localSrc={localPreviewUrls[1]}
					showOverlay={showOverlay}
					order={1}
					demoMode={demoMode}
					qrType={qrType}
					rendersOnMobileShowcasePreview={rendersOnMobileShowcasePreview}
				/>
			) : null}
			{demoMode || thirdImage || localPreviewUrls[2] ? (
				<Image
					image={thirdImage}
					localSrc={localPreviewUrls[2]}
					showOverlay={showOverlay}
					order={2}
					demoMode={demoMode}
					qrType={qrType}
					rendersOnMobileShowcasePreview={rendersOnMobileShowcasePreview}
				/>
			) : null}
		</div>
	)
}
